/**
 * interactive-deduction (Phase 3-3, Task 6.4) Class Trial variant。
 *
 * Danganronpa 風の派手演出。矛盾を「異議あり!」文言で提示し、証拠を「投げる」動詞の
 * submit ボタンで表現する。UI 上の強調 (赤字 / bold) を多用する。
 */

import { useState } from "react";
import { EvidenceSelector, type EvidenceFragment } from "./EvidenceSelector";
import { HpIndicator } from "./HpIndicator";
import type { DeductionPanelVariantProps } from "./types";

export function DeductionPanelClassTrial({
  contradictions,
  memories,
  attempts,
  onSubmit,
  disabled,
}: DeductionPanelVariantProps): JSX.Element {
  const [selectedContradictionId, setSelectedContradictionId] = useState<
    string | null
  >(contradictions[0]?.id ?? null);
  const [selectedMemoryKey, setSelectedMemoryKey] = useState<string | null>(
    null,
  );

  const selectedContradiction = contradictions.find(
    (c) => c.id === selectedContradictionId,
  );

  return (
    <section
      className="flex flex-col gap-4 p-6 bg-gradient-to-br from-slate-900 via-red-950/40 to-slate-900 border-2 border-red-500/50 rounded-lg shadow-xl"
      data-testid="deduction-panel"
      data-variant="class-trial"
    >
      <header className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-black text-red-400 tracking-tight">
            異議あり！
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            矛盾を突く言弾を選んで投げろ
          </p>
        </div>
        <HpIndicator attempts={attempts} />
      </header>

      <div className="flex flex-col gap-2">
        <p className="text-xs uppercase tracking-wider text-slate-400">
          矛盾点
        </p>
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="指摘する矛盾を選択">
          {contradictions.map((c) => (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={c.id === selectedContradictionId}
              disabled={disabled}
              onClick={() => setSelectedContradictionId(c.id)}
              data-testid={`contradiction-${c.id}`}
              className={[
                "text-left px-3 py-2 rounded border-l-4 transition-colors",
                c.id === selectedContradictionId
                  ? "border-red-500 bg-red-900/30 text-red-100 font-bold"
                  : "border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-700/50",
              ].join(" ")}
            >
              {c.text}
            </button>
          ))}
        </div>
        {selectedContradiction?.hint !== undefined ? (
          <p
            className="text-xs italic text-slate-400"
            data-testid="contradiction-hint"
          >
            ヒント: {selectedContradiction.hint}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-xs uppercase tracking-wider text-slate-400">言弾</p>
        <EvidenceSelector
          fragments={memories}
          selectedKey={selectedMemoryKey}
          onSelect={setSelectedMemoryKey}
          disabled={disabled}
        />
      </div>

      <button
        type="button"
        disabled={
          disabled ||
          selectedContradictionId === null ||
          selectedMemoryKey === null
        }
        onClick={() => {
          if (selectedContradictionId !== null && selectedMemoryKey !== null) {
            onSubmit({
              contradictionId: selectedContradictionId,
              memoryFragmentKey: selectedMemoryKey,
            });
          }
        }}
        data-testid="deduction-submit"
        className="px-6 py-3 bg-red-600 hover:bg-red-500 active:scale-[0.98] text-white font-black uppercase tracking-wide rounded disabled:opacity-40 disabled:cursor-not-allowed"
      >
        投げつける！
      </button>
    </section>
  );
}
